import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";

const steps = ["Personal", "Professional", "Links"];

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const [f, setF] = useState({
    full_name: "", location: "", headline: "", years_experience: "",
    skills: "", bio: "", hourly_rate: "",
    portfolio_url: "", linkedin_url: "", github_url: "", resume_url: "",
  });
  const [saving, setSaving] = useState(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const next = () => setStep(Math.min(step + 1, steps.length - 1));
  const prev = () => setStep(Math.max(step - 1, 0));

  const finish = async () => {
    setSaving(true);
    try {
      await api.put("/profile", {
        full_name: f.full_name, location: f.location, headline: f.headline,
        years_experience: Number(f.years_experience) || 0,
        skills: f.skills.split(",").map((s) => s.trim()).filter(Boolean),
        bio: f.bio, hourly_rate: f.hourly_rate,
        portfolio_url: f.portfolio_url, linkedin_url: f.linkedin_url,
        github_url: f.github_url, resume_url: f.resume_url,
      });
      await refresh();
      toast.success("Profile saved");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col">
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <span className="font-display font-semibold flex items-center gap-2"><img src="https://customer-assets.emergentagent.com/job_jd-mailer-pro/artifacts/25t7dqsa_Untitled%20design%20%2835%29.png" alt="Aplica" className="h-7 w-7 rounded-md" />Aplica</span>
          <span className="text-sm text-slate-500">Step {step + 1} of {steps.length}</span>
        </div>
      </div>
      <div className="flex-1 flex items-start justify-center px-6 py-12">
        <div className="w-full max-w-2xl">
          <div className="flex items-center gap-2 mb-10">
            {steps.map((s, i) => (
              <div key={s} className="flex-1 flex items-center gap-2">
                <div className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? "bg-[#0047FF]" : "bg-slate-200"}`} />
              </div>
            ))}
          </div>

          <span className="label-xs">[0{step + 1}] {steps[step]} details</span>
          <h1 className="mt-2 text-3xl md:text-4xl font-display tracking-tighter">
            {step === 0 && "Tell us about yourself."}
            {step === 1 && "Your professional edge."}
            {step === 2 && "Where's your work?"}
          </h1>
          <p className="mt-3 text-slate-600">
            {step === 0 && "The AI uses this to personalize every email."}
            {step === 1 && "What makes you the right fit?"}
            {step === 2 && "Links we'll weave into your outreach."}
          </p>

          <div className="mt-10 bg-white border border-slate-200 rounded-xl p-8 space-y-5">
            {step === 0 && (
              <>
                <Field label="Full name" value={f.full_name} onChange={set("full_name")} testId="onb-fullname" required />
                <Field label="Location (city, country)" value={f.location} onChange={set("location")} testId="onb-location" />
              </>
            )}
            {step === 1 && (
              <>
                <Field label="Professional headline" placeholder="e.g., Senior React Developer" value={f.headline} onChange={set("headline")} testId="onb-headline" required />
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Years of experience" type="number" value={f.years_experience} onChange={set("years_experience")} testId="onb-years" />
                  <Field label="Hourly rate (optional)" placeholder="$60/hr" value={f.hourly_rate} onChange={set("hourly_rate")} testId="onb-rate" />
                </div>
                <Field label="Skills (comma separated)" placeholder="React, Next.js, Node, Postgres" value={f.skills} onChange={set("skills")} testId="onb-skills" />
                <div>
                  <label className="label-xs block mb-1.5">Short bio</label>
                  <textarea rows={4} value={f.bio} onChange={set("bio")}
                    data-testid="onb-bio"
                    placeholder="2-3 sentences on what you build and who you've worked with."
                    className="w-full border border-slate-200 bg-white rounded-md px-4 py-3 focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none" />
                </div>
              </>
            )}
            {step === 2 && (
              <>
                <Field label="Portfolio URL" type="url" value={f.portfolio_url} onChange={set("portfolio_url")} testId="onb-portfolio" placeholder="https://yourportfolio.com" />
                <Field label="LinkedIn URL" type="url" value={f.linkedin_url} onChange={set("linkedin_url")} testId="onb-linkedin" placeholder="https://linkedin.com/in/you" />
                <Field label="GitHub URL (optional)" type="url" value={f.github_url} onChange={set("github_url")} testId="onb-github" placeholder="https://github.com/you" />
                <Field label="Resume URL (optional)" type="url" value={f.resume_url} onChange={set("resume_url")} testId="onb-resume" placeholder="Link to Drive/Dropbox PDF" />
              </>
            )}
          </div>

          <div className="mt-8 flex items-center justify-between">
            <button onClick={prev} disabled={step === 0}
              className="text-slate-600 px-4 py-2 rounded-md hover:bg-slate-100 disabled:opacity-40 flex items-center gap-2"
              data-testid="onb-back">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            {step < steps.length - 1 ? (
              <button onClick={next} className="btn-primary" data-testid="onb-next">
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button onClick={finish} disabled={saving} className="btn-primary" data-testid="onb-finish">
                {saving ? "Saving…" : "Finish"} <Check className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testId, ...rest }) {
  return (
    <div>
      <label className="label-xs block mb-1.5">{label}</label>
      <input
        data-testid={testId}
        {...rest}
        className="w-full border border-slate-200 bg-white rounded-md px-4 py-3 focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none"
      />
    </div>
  );
}
